/* eslint-disable max-len */
/* eslint-disable jsx-a11y/anchor-is-valid */
/**
@format
@flow
*/

import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Button } from '@ziynx/components/buttons';
import Modal from '@ziynx/components/modals';
import Card from '@ziynx/components/cards';
import { Heading } from '@ziynx/components/typography';
import { useForm, Controller } from 'react-hook-form';
import Input from '@ziynx/components/inputs';
import { Form, FormRow } from '@ziynx/components/forms';
import { eventNames } from '@ziynx/analytics';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { Helmet } from 'react-helmet-async';
import { ContentContainer } from '@ziynx/components/layout';
import { useParams } from 'react-router-dom';
import type { VideoUploadConfig } from '@ziynx/flow-types/companyProfile';
import type { ManagedProps } from '@ziynx/flow-types/appManager';
import {
  withAppManager, useNotification, /* nameInputsRegex */ getMeasures,
} from '@ziynx/components/utilities';
import UploadVideoModal from '@ziynx/pages/Modals/UploadVideo';
import Link from '@ziynx/components/links';
import Colors from '@ziynx/theme/colors';
import ActionTypes from '@ziynx/action-types';
import type { Viewports } from '@ziynx/flow-types/globals';
import DestructiveModal from '../Modals/DestructiveModal';

type Props = $ReadOnly<{|
  /**
   * Viewport
   */
  viewport: Viewports,
  /**
   * Navigation stack
   */
  history: {
    push: (pathName: string, params?: { from: string }) => void,
    goBack: () => void,
    location: {
      state: {
        from: string,
      }
    },
  },

|}>;

const ModalConstants = {
  modalPadding: 32, // top or bottom
  titlePaddingBottom: 32,
  titleHeight: 30, // 30 + 32
  cropperButtonGroup: 130, // height
  cropperButtonGroupMargin: 32,
  editCropMargin: 46,
};

const MODAL_PADDING = {
  mobileSmall: `${ModalConstants?.modalPadding}px 16px`,
  mobile: `${ModalConstants?.modalPadding}px 16px`,
  tablet: `${ModalConstants?.modalPadding}px 16px`,
  desktopSmall: `${ModalConstants?.modalPadding}px 24px`,
  default: `${ModalConstants?.modalPadding}px 48px`,
};

const _modalWidth = (viewport) => {
  switch (viewport) {
  case 'mobile':
    return '333px';
  case 'mobileSmall':
    return '240px';
  default:
    return '488px';
  }
};

const modalButtonWidth = (viewport) => {
  switch (viewport) {
  case 'mobile':
    return '50%';
  case 'mobileSmall':
    return '100%';
  default:
    return '40%';
  }
};

const PageWrapper = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: ${({ isMobileSmall }) => (isMobileSmall ? '40px 16px 60px' : '40px 40px 60px')};
`;

const PageContentWrapper = styled.div`
  width: 100%;
  max-width: 688px;
  fieldset {
    all: unset;
    width: 100%;
  }
`;

const PlayVideoModal = styled(Modal)`
  padding: ${({ viewport }) => getMeasures(MODAL_PADDING, viewport)};
  overflow: hidden;
`;

const CheckboxWrapper = styled.div`
  margin-left: 12px;
`;

const ModalButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  text-align: center;
  flex-direction: ${({ isMobileSmall }) => (isMobileSmall ? 'column' : 'row')};
`;

const ModalButton = styled(Button)`
  width: ${({ viewport }) => modalButtonWidth(viewport)};
  ${({ isMobileSmall, firstChild }) => !isMobileSmall && firstChild && 'margin-right: 16px;'}
  ${({ isMobileSmall, firstChild }) => isMobileSmall && firstChild && 'margin-bottom: 16px;'}
`;

const VideoSectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  height: 100%;
`;

const CardVideoWrapper = styled.div`
  margin-bottom: 32px;
`;

const LinkWrapper = styled.div`
  margin-bottom: 24px;
`;

const DiscardModal = styled(Modal)`
  padding: 24px;
  height: 180px;
  width: ${({ viewport }) => _modalWidth(viewport)};
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: center;
  height: 100%;
`;

const StyledHeading = styled(Heading)`
  padding-bottom: 16px;
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: ${({ isMobileSmall, viewport }) => (isMobileSmall || viewport === 'mobile' ? 'column' : 'row')};
  justify-content: space-between;
  align-items: center;
  margin-top: 40px;
`;

const StyledButton = styled(Button)`
  width: ${({ isMobileSmall }) => (isMobileSmall ? '100%' : '50%')};
  &:first-of-type {
    ${({ isMobileSmall }) => (isMobileSmall ? 'margin-bottom: 16px;' : 'margin-right: 16px;')}
  }
`;

const StyledFormRow = styled(FormRow)`
  ${({ top }) => top && 'margin-top: 40px;'};
`;

const VideoQuestion = (props: ManagedProps<Props>): React$Node => {
  const { viewport, history } = props;
  const { contentId } = useParams();
  const { notificationDispatch } = useNotification();
  const isMobileSmall = viewport === 'mobileSmall';

  const isEdit = !!contentId;
  const goBackTitle = history?.location?.state?.from || 'Content';

  const dispatch = useDispatch();

  const statementService = useSelector(({ statements }) => statements || {}, shallowEqual);

  const statementItemService = useSelector(
    ({ statementItem }) => (
      isEdit ? statementItem[contentId]
        : statementItem.POST_QUESTION_API), shallowEqual,
  );

  const {
    control,
    errors,
    clearErrors,
    handleSubmit,
    formState: { dirtyFields, isDirty, isSubmitSuccessful },
    isSubmitting,
    reset,
    register,
  } = useForm();

  const mediaService = useSelector(({ media }) => {
    const videoPending = media.postContent || {};
    const videoSources = videoPending?.sources || {};
    const thumbnail = videoSources?.thumbnail_optimized?.url || videoSources?.thumbnail?.url;
    const croppedVideo = {
      src: videoSources?.mp4?.url ?? '',
      thumbnail: thumbnail ?? '',
    };

    return {
      ...media,
      videoPending,
      croppedVideo,
    };
  }, shallowEqual);

  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [discardModalOpen, setDiscardModalOpen] = useState(false);
  const [playModalOpen, setPlayModalOpen] = useState(false);
  const [videoUploadConfig, setVideoConfig] = useState({});
  const [removeModalOpen, setRemoveModalOpen] = useState(false);
  const [uploadObj, setuploadObj] = useState({});
  // isSubmitting is not accessible outside the Controller
  const [formIsSubmitting, setFormIsSubmitting] = useState(false);

  const getMediaId = (obj) => {
    setuploadObj(obj);
  };

  const _closeVideoModal = (): void => {
    setUploadModalOpen(false);
  };

  const _togglePlayModal = (): void => {
    dispatch({
      type: ActionTypes.analytics.logEvent.START,
      name: eventNames.played_video,
    });
    if (!playModalOpen) {
      setPlayModalOpen(true);
    } else {
      setPlayModalOpen(false);
    }
  };

  const _openVideoModal = (uploadConfig: VideoUploadConfig): void => {
    setVideoConfig(uploadConfig);
    setUploadModalOpen(true);
  };

  useEffect(() => {
    if (isEdit) {
      dispatch({
        type: ActionTypes.analytics.logEvent.START,
        name: eventNames.navigated_edit_question,
        properties: {
          referrer: 'Add Empty CTA',
        },
      });
    } else {
      dispatch({
        type: ActionTypes.analytics.logEvent.START,
        name: eventNames.navigated_add_new_question,
        properties: {
          referrer: 'Add Empty CTA',
        },
      });
    }
  }, [isEdit]);

  useEffect(() => {
    if (isEdit) {
      dispatch({
        type: ActionTypes.statementItem.getQuestion.START,
        id: contentId,
      });
    }
  }, [isEdit]);

  useEffect(() => {
    if (isEdit && statementItemService?.success) {
      reset({
        question: statementItemService?.question,
        instructionStepOne: statementItemService?.instructions?.[0],
        instructionStepTwo: statementItemService?.instructions?.[1],
        instructionStepThree: statementItemService?.instructions?.[2],
      }, {
        dirtyFields: false,
      });
    } else return undefined;
  }, [statementItemService?.success, statementItemService?.error, isEdit]);

  useEffect(() => {
    if ((!statementItemService?.fetching && statementItemService?.success && !isEdit)
    || (!statementItemService?.patching && statementItemService?.patchSuccess)) {
      setFormIsSubmitting(false);
      if (isEdit) {
        dispatch({ type: ActionTypes.statements.patchedQuestion });
      }
      dispatch({ type: ActionTypes.statementItem.clear, id: statementItemService?._id });
      history.push('/content/', { from: '/content/video-question' });
    }
  }, [
    statementItemService?.fetching,
    statementItemService?.success,
    statementItemService?.patching,
    isEdit,
  ]);

  const [nilOnly, setNilOnly] = useState(statementItemService?.featureTag === 'nil');

  const INPUT_FIELDS = [
    'question',
    'instructionStepOne',
    'instructionStepTwo',
    'instructionStepThree',
    'nilOnly',
  ];

  useEffect(() => {
    if ((statementItemService?.featureTag === 'nil') !== nilOnly) setNilOnly(statementItemService?.featureTag === 'nil');
  }, [statementItemService?.featureTag]);

  const _getPatchOps = ({ data }) => {
    const ops = [];
    const instructions = [
      data.instructionStepOne, data.instructionStepTwo, data.instructionStepThree];

    if (statementService?.featureTag !== 'nil' && nilOnly) {
      ops.push({
        op: 'replace',
        path: 'featureTag',
        value: 'nil',
      });
    } else if (statementService?.featureTag === 'nil' && !nilOnly) {
      ops.push({
        op: 'remove',
        path: 'featureTag',
      });
    }

    INPUT_FIELDS.forEach((field) => {
      if (!dirtyFields[field]) return;
      if (field === 'question' && data?.question) {
        ops.push(
          {
            op: 'replace',
            path: 'question',
            value: data?.question?.trim(),
          },
        );
      } else if (field === 'instructionStepOne' && data?.instructionStepOne) {
        instructions[0] = data?.instructionStepOne;
        ops.push(
          {
            op: 'replace',
            path: 'instructions',
            value: instructions,
          },
        );
      } else if (field === 'instructionStepTwo') {
        instructions[1] = data?.instructionStepTwo;
        ops.push({
          op: 'replace',
          path: 'instructions',
          value: instructions,
        });
      } else if (field === 'instructionStepThree') {
        instructions[2] = data?.instructionStepThree;
        ops.push({
          op: 'replace',
          path: 'instructions',
          value: instructions,
        });
      }
    });
    return ops;
  };

  const onSubmit = async (data: {question: string,
      instructionStepOne: string,
      instructionStepTwo: string,
      instructionStepThree: string,
    }) => {
    const patchOps = isEdit ? _getPatchOps({ data }) : null;
    const {
      question, instructionStepOne, instructionStepTwo, instructionStepThree,
    } = data;

    setFormIsSubmitting(true);

    if (isEdit) {
      dispatch({
        type: ActionTypes.statementItem.patchQuestion.START,
        body: patchOps,
        id: contentId,
      });
    } else {
      dispatch({
        type: ActionTypes.analytics.logEvent.START,
        name: eventNames.created_video_question,
      });
      dispatch({
        type: ActionTypes.statementItem.postQuestion.START,
        body: {
          question,
          variant: 'video',
          creatorType: 'company',
          instructions: [
            instructionStepOne,
            instructionStepTwo,
            instructionStepThree,
          ],
          inspirationVideo: uploadObj?.assetId,
          featureTag: !nilOnly ? '' : 'nil',
        },
      });
    }
    if (statementItemService?.success) {
      notificationDispatch({
        type: ActionTypes.notifications.add.START,
        payload: {
          variant: 'success',
          content: 'Content has been saved successfully.',
        },
      });
      setFormIsSubmitting(false);
    }
    if (statementItemService?.error) {
      notificationDispatch({
        type: ActionTypes.notifications.add.START,
        payload: {
          variant: 'error',
          content: 'Something went wrong. Please try again.',
        },
      });
      setFormIsSubmitting(false);
    }
  };

  const onClickDestructive = (): void => {
    if (!contentId) {
      setuploadObj({});
      setRemoveModalOpen(false);
      return;
    }
    dispatch({
      type: ActionTypes.analytics.logEvent.START,
      name: eventNames.removed_video_success,
    });
    dispatch({
      type: ActionTypes.statementItem.patchQuestion.START,
      body: [
        {
          op: 'remove',
          path: 'inspirationVideo',
        },
      ],
      id: contentId,
    });
    dispatch({ type: ActionTypes.statementItem.clear });
  };

  const _renderCheckbox = () => (
    <CheckboxWrapper>
      <Input
        {...register({ name: 'nilOnly' })}
        variant="checkbox"
        squircle
        disabled={statementItemService?.fetching}
        inputName="nilOnly"
        label="NIL application only"
        labelColor={Colors.GrayVeryDark}
        previouslyChecked={statementItemService?.featureTag === 'nil'}
        onChange={() => {
          const flipped = !nilOnly;
          control?.setValue('nilOnly', flipped, { shouldDirty: (statementItemService?.featureTag === 'nil') !== flipped });
          setNilOnly(flipped);
        }}
      />
    </CheckboxWrapper>
  );

  return (
    <>
      <Helmet>
        <title>Customize Video | ZIYNX</title>
      </Helmet>
      <ContentContainer isVertical>
        <UploadVideoModal
          open={uploadModalOpen}
          videoUploadConfig={videoUploadConfig}
          onClose={_closeVideoModal}
          getMediaId={getMediaId}
        />
        { !mediaService?.contentPost?.fetching
          ? (
            <PlayVideoModal open={playModalOpen}>
              <VideoSectionWrapper>
                {!mediaService?.contentPost?.fetching ? (
                  <CardVideoWrapper>
                    <Card
                      variant="video"
                      width="400px"
                      src={isEdit
                        ? statementItemService?.inspirationVideo?.sources?.mp4?.url
                        : uploadObj?.sources?.mp4?.url}
                      imageSource={isEdit
                      // eslint-disable-next-line
                          ? statementItemService?.inspirationVideo?.sources?.thumbnail_optimized?.url
                        || statementItemService?.inspirationVideo?.sources?.thumbnail.url
                        : uploadObj?.sources?.thumbnail_optimized?.url
                        || uploadObj?.sources?.thumbnail?.url}
                    />
                  </CardVideoWrapper>
                ) : null}
                <Button variant="primary" inverted onClick={_togglePlayModal}>Close Modal</Button>
              </VideoSectionWrapper>
            </PlayVideoModal>
          ) : null }
        <PageWrapper isMobileSmall={isMobileSmall}>
          <PageContentWrapper>
            <DestructiveModal
              heading={{
                text: 'Are you sure you want to remove the video?',
              }}
              body={{
                text: 'This action cannot be undone.',
              }}
              open={removeModalOpen}
              buttonGroup={{
                destructive: {
                  children: 'Remove',
                  onClick: onClickDestructive,
                },
                primary: {
                  children: 'Cancel',
                  onClick: () => setRemoveModalOpen(false),
                },
              }}
            />
            <LinkWrapper>
              <Link
                variant="caret"
                color={Colors.GrayDark}
                onClick={(isDirty && !isSubmitSuccessful)
                  ? () => setDiscardModalOpen(true) : () => {
                    history.goBack();
                  }}
              >
                Back to
                {' '}
                {goBackTitle}
              </Link>
            </LinkWrapper>
            <Form
              formID="customize-video-question"
              title="Customize video question"
              width="100%"
            >
              <fieldset disabled={isSubmitting}>
                <FormRow paddingBottom="40px">
                  <Controller
                    control={control}
                    render={({
                      onFocus, onChange, onBlur, value,
                    }) => {
                      const onInputChange = (event: SyntheticInputEvent<HTMLInputElement>): void => {
                        (event?.target: HTMLInputElement);
                        onChange(event);
                      };

                      const onInputFocus = (): any => {
                        if (errors?.question) clearErrors('question');
                        return onFocus;
                      };

                      return (
                        <Input
                          variant="text"
                          autoComplete="off"
                          error={!!errors?.question}
                          disabled={statementItemService?.fetching}
                          errorLabel={errors?.question?.message}
                          inputName="question"
                          charLimit={65}
                          maxLength={65}
                          initialCount={statementItemService?.question?.length || 0}
                          required
                          label="Question"
                          onBlur={onBlur}
                          onChange={onInputChange}
                          onFocus={onInputFocus}
                          value={value}
                        />
                      );
                    }}
                    name="question"
                    defaultValue={isEdit ? statementItemService?.question || ' ' : ''}
                    rules={{
                      required: 'Question required.',
                      validate: (value) => !!value.trim(),
                    }}
                  />
                </FormRow>
                <FormRow paddingBottom="40px">
                  <Controller
                    control={control}
                    render={({
                      onFocus, onChange, onBlur, value,
                    }) => {
                      const onInputChange = (
                        event: SyntheticInputEvent<HTMLInputElement>,
                      ): void => {
                        (event?.target: HTMLInputElement);
                        onChange(event);
                      };
                      const onInputFocus = (): any => {
                        if (errors?.instructionStepOne) clearErrors('instructionStepOne');
                        return onFocus;
                      };
                      return (
                        <Input
                          variant="textarea"
                          autoComplete="off"
                          error={!!errors?.instructionStepOne}
                          errorLabel={errors?.instructionStepOne?.message}
                          inputName="instructionStepOne"
                          charLimit={135}
                          maxLength={135}
                          initialCount={statementItemService?.instructions?.[0]?.length || 0}
                          disabled={statementItemService?.fetching}
                          required
                          onChange={onInputChange}
                          onFocus={onInputFocus}
                          onBlur={onBlur}
                          label="Instruction: Step 1"
                          value={value}
                        />
                      );
                    }}
                    name="instructionStepOne"
                    defaultValue={statementItemService?.instructions?.[0] || ''}
                    rules={{
                      required: 'Instruction: Step 1 required',
                      validate: (value) => !!value.trim(),
                    }}
                  />
                </FormRow>
                <StyledFormRow top paddingBottom="40px">
                  <Controller
                    control={control}
                    render={({
                      onFocus, onChange, onBlur, value,
                    }) => {
                      const onInputChange = (event: SyntheticInputEvent<HTMLInputElement>): void => {
                        (event?.target: HTMLInputElement);
                        onChange(event);
                      };
                      const onInputFocus = (): any => {
                        if (errors?.instructionStepTwo) clearErrors('instructionStepTwo');
                        return onFocus;
                      };
                      return (
                        <Input
                          variant="textarea"
                          autoComplete="off"
                          error={!!errors?.instructionStepTwo}
                          errorLabel={errors?.instructionStepTwo?.message}
                          charLimit={135}
                          maxLength={135}
                          initialCount={statementItemService?.instructions?.[1]?.length || 0}
                          inputName="instructionStepTwo"
                          disabled={statementItemService?.fetching}
                          onChange={onInputChange}
                          onFocus={onInputFocus}
                          onBlur={onBlur}
                          label="Instruction: Step 2"
                          value={value}
                        />
                      );
                    }}
                    name="instructionStepTwo"
                    defaultValue={isEdit ? statementItemService?.instructions?.[1] || '' : ''}
                  />
                </StyledFormRow>
                <StyledFormRow top paddingBottom="80px">
                  <Controller
                    control={control}
                    render={({
                      onFocus, onChange, onBlur, value,
                    }) => {
                      const onInputChange = (event: SyntheticInputEvent<HTMLInputElement>): void => {
                        (event?.target: HTMLInputElement);
                        onChange(event);
                      };
                      const onInputFocus = (): any => {
                        if (errors?.instructionStepThree) clearErrors('instructionStepThree');
                        return onFocus;
                      };
                      return (
                        <Input
                          variant="textarea"
                          autoComplete="off"
                          error={!!errors?.instructionStepThree}
                          errorLabel={errors?.instructionStepThree?.message}
                          charLimit={135}
                          maxLength={135}
                          initialCount={statementItemService?.instructions?.[2]?.length || 0}
                          inputName="instructionStepThree"
                          disabled={statementItemService?.fetching}
                          onChange={onInputChange}
                          onFocus={onInputFocus}
                          onBlur={onBlur}
                          label="Instruction: Step 3"
                          value={value}
                        />
                      );
                    }}
                    name="instructionStepThree"
                    defaultValue={isEdit ? statementItemService?.instructions?.[2] || '' : ''}
                  />
                </StyledFormRow>
                <FormRow height="100%" paddingBottom="32px">
                  {statementItemService?.fetching ? null : _renderCheckbox(control)}
                </FormRow>
              </fieldset>
            </Form>
            <Card
              variant="videoPreview"
              title="Inspiration video"
              isLoading={mediaService?.contentPost?.fetching}
              image={!mediaService?.contentPost?.fetching ? {
                imageSource: isEdit
                  ? statementItemService?.inspirationVideo?.sources?.thumbnail_optimized?.url
                  || statementItemService?.inspirationVideo?.sources?.thumbnail?.url
                  : uploadObj?.sources?.thumbnail_optimized?.url || uploadObj?.sources?.thumbnail,
              } : undefined}
              link={{
                children: 'Add Inspiration video',
                onClick: () => {
                  dispatch({
                    type: ActionTypes.analytics.logEvent.START,
                    name: eventNames.added_inspiration_video,
                  });
                  _openVideoModal({
                    destination: 'contentVideo',
                    mediaUploadReferenceId: contentId ?? 'contentPost',
                  });
                },
                disabled: formIsSubmitting,
              }}
              rightAction={{
                disabled: formIsSubmitting,
                popover: {
                  position: 'bottomLeft',
                  links: [
                    {
                      children: 'Play video',
                      onClick: _togglePlayModal,
                    },
                    {
                      children: 'Change video',
                      onClick: () => {
                        dispatch({
                          type: ActionTypes.analytics.logEvent.START,
                          name: eventNames.changed_video_question,
                        });
                        _openVideoModal({
                          destination: 'contentVideo',
                          mediaUploadReferenceId: contentId ?? 'contentPost',
                          isChange: true,
                        });
                      },
                    },
                    {
                      children: 'Remove video',
                      onClick: () => {
                        dispatch({
                          type: ActionTypes.analytics.logEvent.START,
                          name: eventNames.removed_video,
                        });
                        setRemoveModalOpen(true);
                      },
                      color: Colors.SecondaryRed,
                    },
                  ],
                },
              }}
            />
            {!isEdit && (
              <ButtonWrapper isMobileSmall={isMobileSmall}>
                <StyledButton
                  isMobileSmall={isMobileSmall}
                  onClick={handleSubmit(onSubmit)}
                  disabled={
                    Object.keys(dirtyFields).length === 0
                    || Object.keys(errors).length > 0
                    || statementItemService?.fetching
                    || formIsSubmitting
                  }
                  isLoading={statementItemService?.fetching}
                  width="50%"
                >
                  Create Question
                </StyledButton>
                <StyledButton
                  isMobileSmall={isMobileSmall}
                  disabled={formIsSubmitting}
                  variant="primary"
                  width="50%"
                  inverted
                  onClick={(isDirty && !isSubmitSuccessful)
                    ? () => setDiscardModalOpen(true) : () => {
                      history.goBack();
                    }}
                >
                  Cancel
                </StyledButton>
              </ButtonWrapper>
            )}
            {isEdit && (
              <ButtonWrapper isMobileSmall={isMobileSmall} viewport={viewport}>
                <StyledButton
                  isMobileSmall={isMobileSmall}
                  disabled={
                    (Object.keys(dirtyFields).length === 0
                    && nilOnly === (statementItemService?.featureTag === 'nil'))
                    || Object.keys(errors).length > 0
                    || statementItemService?.fetching
                    || formIsSubmitting
                  }
                  onClick={handleSubmit(onSubmit)}
                  isLoading={statementItemService?.fetching}
                >
                  Save
                </StyledButton>
                <StyledButton
                  isMobileSmall={isMobileSmall}
                  disabled={formIsSubmitting}
                  onClick={(isDirty && !isSubmitSuccessful)
                    ? () => setDiscardModalOpen(true) : () => {
                      history.goBack();
                    }}
                  variant="primary"
                  inverted
                >
                  Cancel
                </StyledButton>
              </ButtonWrapper>
            )}
          </PageContentWrapper>
        </PageWrapper>
        <DiscardModal open={discardModalOpen} viewport={viewport}>
          <ModalContent>
            <StyledHeading small>Are you sure you want to discard your changes?</StyledHeading>
            <ModalButtonWrapper isMobileSmall={isMobileSmall}>
              <ModalButton
                width="100%"
                viewport={viewport}
                isMobileSmall={isMobileSmall}
                firstChild
                onClick={() => {
                  history.goBack();
                }}
              >
                Back to
                {' '}
                {goBackTitle}

              </ModalButton>
              <ModalButton
                viewport={viewport}
                isMobileSmall={isMobileSmall}
                inverted
                width="100%"
                onClick={() => setDiscardModalOpen(false)}
              >
                Cancel
              </ModalButton>
            </ModalButtonWrapper>
          </ModalContent>
        </DiscardModal>
      </ContentContainer>
    </>
  );
};

export default (withAppManager(VideoQuestion): React$ComponentType<ManagedProps<Props>>);
